import axios from "axios";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { API } from "../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../helpers/errorHandling";
import { ISSUES_GLOBAL } from "../../modules/issues/locales/namespaces";
import useUserAction from "../user/useUserAction";
import useUserData from "../user/useUserData";

const useFiltersData = () => {
	const { t } = useTranslation(ISSUES_GLOBAL)
	const { token, isLoggedIn } = useUserData()
	const {userLogout} = useUserAction()
	const [loading, setLoading] = useState(false)
	const [filtersData, setFiltersData] = useState({
		customers: [],
		sites: [],
		statuses: [],
		types: [],
		categories: [],
		assignees: [],
		kinds: [],
		technicians: [],
		internalAssignments: []
	})
	
	const getFiltersData = useCallback(() => {
		setLoading(true)
		axios({
			method: 'get',
			url: `${API}/en/issues/filter/data`,
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
		}).then(res => {
			setFiltersData({
				customers: res.data.customers,
				sites: res.data.sites,
				statuses: res.data.statuses,
				types: res.data.types,
				categories: res.data.categories,
				assignees: res.data.assignees,
				kinds: res.data.kinds ?? [],
				technicians: res.data.technicians ?? [],
				internalAssignments: res.data.internalAssignments ?? []
			})
			setLoading(false)
		}).catch((error) => {
			setLoading(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout])
	
	useEffect(() => {
		getFiltersData()
	}, [])
	
	const customers = useMemo(() => (filtersData.customers), [filtersData.customers])
	const sites = useMemo(() => (filtersData.sites), [filtersData.sites])
	const statuses = useMemo(() => (_.orderBy(filtersData.statuses, ['Description'], ['asc'])), [filtersData.statuses])
	const types = useMemo(() => (_.orderBy(filtersData.types, ['Description'], ['asc'])), [filtersData.types])
	const categories = useMemo(() => _.orderBy(filtersData.categories, ['Description'], ['asc'] ), [filtersData.categories])
	const assignees = useMemo(() => _.orderBy(filtersData.assignees, ['Description'], ['asc']), [filtersData.assignees])
	const kinds = useMemo(() => _.orderBy(filtersData.kinds, ['Description'], ['asc']), [filtersData.kinds])
	const technicians = useMemo(() => _.orderBy(filtersData.technicians, ['Description'], ['asc']), [filtersData.technicians])
	const internalAssignments = useMemo(() => _.orderBy(filtersData.internalAssignments, ['Description'], ['asc']), [filtersData.internalAssignments])

	return {
		loading,
		customers,
		sites,
		statuses,
		types,
		categories,
		assignees,
		kinds,
		technicians,
		internalAssignments,
		setFiltersData
	}
}

export default useFiltersData